import { Controller } from "@hotwired/stimulus"

type Marker = { kind: string; at: string; label: string; n?: number }
type Bucket = { t0: string; n: number; cum: number }
type SpineRow = { kind: string; at: string; label: string; n?: number }
type Series = {
  meta: {
    start: string
    end: string
    now: string
    eligible: number
    voted: number
    turnout_bps: number
    bucket_seconds: number
  }
  markers: Marker[]
  buckets: Bucket[]
  spine: SpineRow[]
}

const COLORS: Record<string, string> = {
  start: "#17BF3C",
  reminder: "#06AED5",
  extension: "#FF8002",
  quorum_reached: "#800080",
  everyone_voted: "#006b5b",
  end: "#172837",
  bucket: "#172837",
  quiet_range: "#949493",
}

export default class extends Controller {
  static targets = ["chart", "tooltip", "turnout", "legend", "rail", "spine", "payload"]

  declare readonly chartTarget: SVGSVGElement
  declare readonly tooltipTarget: HTMLElement
  declare readonly turnoutTarget: HTMLElement
  declare readonly legendTarget: HTMLElement
  declare readonly railTarget: HTMLElement
  declare readonly spineTarget: HTMLOListElement
  declare readonly payloadTarget: HTMLScriptElement
  private viewFrom = 0
  private viewTo = 0

  connect(): void {
    this.fit()
    window.addEventListener("resize", this.draw)
    this.chartTarget.addEventListener("mousemove", this.onMove)
    this.chartTarget.addEventListener("mouseleave", this.onLeave)
  }

  disconnect(): void {
    window.removeEventListener("resize", this.draw)
    this.chartTarget.removeEventListener("mousemove", this.onMove)
    this.chartTarget.removeEventListener("mouseleave", this.onLeave)
  }

  private draw = (): void => {
    const series = this.series()
    if (!series?.meta) return

    const eligible = series.meta.eligible || 0
    const pct = (series.meta.turnout_bps / 100).toFixed(2)
    this.turnoutTarget.textContent = `${Number(series.meta.voted).toLocaleString()} of ${Number(eligible).toLocaleString()} · ${pct}%`
    this.legendTarget.innerHTML = [
      ["#172837", "Votes / bucket"],
      ["#FF8002", "Cumulative turnout"],
    ]
      .map(([color, label]) => `<span><i style="display:inline-block;width:10px;height:10px;background:${color};margin-right:4px"></i>${label}</span>`)
      .join("")
    this.railTarget.innerHTML = (series.markers || [])
      .map((marker) => `<span class="god-vote-timeline__chip">${marker.label}</span>`)
      .join("")

    const axisStart = Date.parse(series.meta.start)
    const axisEnd = Date.parse(series.meta.end || series.meta.now)
    if (!this.viewFrom || !this.viewTo) {
      this.viewFrom = axisStart
      this.viewTo = axisEnd
    }
    const now = Date.parse(series.meta.now)
    const buckets = series.buckets || []
    const svg = this.chartTarget
    const width = svg.clientWidth || 900
    const height = 320
    const pad = { l: 48, r: 48, t: 24, b: 36 }
    const innerW = width - pad.l - pad.r
    const innerH = height - pad.t - pad.b
    const span = Math.max(this.viewTo - this.viewFrom, 1)
    const xOf = (ms: number) => pad.l + ((ms - this.viewFrom) / span) * innerW
    const maxN = Math.max(1, ...buckets.map((bucket) => Number(bucket.n) || 0))
    const bucketMs = (series.meta.bucket_seconds || 3600) * 1000
    const bars = buckets
      .map((bucket) => {
        const t0 = Date.parse(bucket.t0)
        const x = xOf(t0)
        const w = Math.max(2, xOf(t0 + bucketMs) - x - 1)
        const h = (Number(bucket.n) / maxN) * innerH
        const y = pad.t + innerH - h
        return `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="#172837" opacity="0.85"></rect>`
      })
      .join("")
    const path = buckets
      .map((bucket, index) => {
        const t0 = Date.parse(bucket.t0)
        const x = xOf(t0 + bucketMs / 2)
        const bps = eligible === 0 ? 0 : Math.trunc((Number(bucket.cum) * 10000) / eligible)
        const y = pad.t + innerH - (bps / 10000) * innerH
        return `${index ? "L" : "M"}${x},${y}`
      })
      .join(" ")
    const markers = (series.markers || [])
      .map((marker) => {
        const x = xOf(Date.parse(marker.at))
        if (x < pad.l || x > pad.l + innerW) return ""
        const color = COLORS[marker.kind] || "#172837"
        return `<line x1="${x}" x2="${x}" y1="${pad.t}" y2="${pad.t + innerH}" stroke="${color}" stroke-dasharray="3 4"></line>
          <circle cx="${x}" cy="${pad.t + 6}" r="5" fill="${color}"><title>${marker.label}</title></circle>`
      })
      .join("")
    const nowX = xOf(now)
    const nowLine =
      nowX >= pad.l && nowX <= pad.l + innerW
        ? `<line x1="${nowX}" x2="${nowX}" y1="${pad.t}" y2="${pad.t + innerH}" stroke="#ff8002" stroke-dasharray="6 4"></line>`
        : ""

    svg.setAttribute("viewBox", `0 0 ${width} ${height}`)
    svg.innerHTML = `
      <line x1="${pad.l}" y1="${pad.t + innerH}" x2="${pad.l + innerW}" y2="${pad.t + innerH}" stroke="#ead9c8"></line>
      ${bars}
      <path d="${path}" fill="none" stroke="#FF8002" stroke-width="2.4"></path>
      ${markers}
      ${nowLine}
    `

    this.spineTarget.innerHTML = (series.spine || [])
      .map((row) => `<li>${row.label}${row.n != null ? ` · n=${row.n}` : ""}</li>`)
      .join("")
  }

  private onMove = (event: MouseEvent): void => {
    const series = this.series()
    const buckets = series.buckets || []
    if (!buckets.length) return
    const rect = this.chartTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    const start = this.viewFrom || Date.parse(series.meta.start)
    const end = this.viewTo || Date.parse(series.meta.end || series.meta.now)
    const t = start + ((x - 48) / Math.max(rect.width - 96, 1)) * (end - start)
    const bucket = buckets.reduce((best, current) =>
      Math.abs(Date.parse(current.t0) - t) < Math.abs(Date.parse(best.t0) - t) ? current : best
    )
    this.tooltipTarget.hidden = false
    this.tooltipTarget.style.left = `${x}px`
    this.tooltipTarget.style.top = `${event.clientY - rect.top}px`
    this.tooltipTarget.textContent = `${bucket.n} votes · cum ${bucket.cum}`
  }

  private series(): Series {
    if (this.payloadTarget?.textContent) {
      return JSON.parse(this.payloadTarget.textContent) as Series
    }
    return { meta: { start: "", end: "", now: "", eligible: 0, voted: 0, turnout_bps: 0, bucket_seconds: 3600 }, markers: [], buckets: [], spine: [] }
  }

  private onLeave = (): void => {
    this.tooltipTarget.hidden = true
  }

  zoomIn(): void {
    this.zoom(0.6)
  }

  zoomOut(): void {
    this.zoom(1.5)
  }

  fit(): void {
    const series = this.series()
    if (!series?.meta) return
    this.viewFrom = Date.parse(series.meta.start)
    this.viewTo = Date.parse(series.meta.end || series.meta.now)
    this.draw()
  }

  jumpToNow(): void {
    const series = this.series()
    if (!series?.meta) return
    const now = Date.parse(series.meta.now)
    const hour = 3600000
    this.viewFrom = now - 18 * hour
    this.viewTo = now + 6 * hour
    this.draw()
  }

  private zoom(factor: number): void {
    const series = this.series()
    if (!series?.meta) return
    const axisStart = Date.parse(series.meta.start)
    const axisEnd = Date.parse(series.meta.end || series.meta.now)
    const mid = (this.viewFrom + this.viewTo) / 2
    let half = ((this.viewTo - this.viewFrom) / 2) * factor
    half = Math.max(2 * 3600000, Math.min(half, (axisEnd - axisStart) / 2))
    this.viewFrom = Math.max(axisStart, mid - half)
    this.viewTo = Math.min(axisEnd + 6 * 3600000, mid + half)
    this.draw()
  }
}
